/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import Button from 'react-bootstrap/Button'
import moment from 'moment'

import { TableWrapper } from 'components/atoms/TableWrapper'
import DataTable from 'components/atoms/DataTable'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import { Can } from 'components/lab/Can'
import { UserPermission } from 'components/lab/user/permissions'
import { useGetIncomingDocumentsQuery, useDeleteIncomingDocumentMutation } from 'store/services/incomingDocuments'
import { useAttachIncomingDocumentMutation } from 'store/services/labOrder'
import { UploadType } from 'store/types'
import { confirm } from 'components/molecules/Confirmation'
import { AssignDialog } from './AssignDialog'
import CreateOrderDialog from './createOrder/CreateOrderDialog'
import { IncomingDocumentStatus, IncomingDocumentType } from './types'

const StyledTableWrapper = styled(TableWrapper)`
  thead {
    z-index: 1;
  }
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.6rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

const IncomingDocuments = () => {
  const [assignDialogFaxId, setAssignDialogFaxId] = useState<string>()
  const [createDialogFaxId, setCreateDialogFaxId] = useState<string>()

  const { data: faxes, isLoading } = useGetIncomingDocumentsQuery(undefined, {
    pollingInterval: 30000,
    refetchOnFocus: true,
  })
  const [attachIncomingDocument] = useAttachIncomingDocumentMutation()
  const [deleteFax] = useDeleteIncomingDocumentMutation()

  const incoming = useMemo(
    () => ({
      page: 0,
      items:
        faxes?.map((f): IncomingDocumentType => ({
          uuid: f.uuid,
          mediaUrl: f.url,
          faxType: f.faxType,
          source: f.source,
          status: f.status,
          patientData: f.patientData,
          orderId: f.orderId,
          mailData: f.mailData,
          error: f.error,
          createdAt: moment(f.createdAt).format('lll'),
        })) ?? [],
      totalItems: faxes?.length ?? 0,
      totalPages: 1,
    }),
    [faxes],
  )

  const currentFax = incoming.items.find((f) => f.uuid === createDialogFaxId)
  const currentAssignItem = incoming.items.find((f) => f.uuid === assignDialogFaxId)

  const attachToOrder = (
    orderId: number,
    faxUuid: string,
    uploadTypes: UploadType[],
    rotation?: number,
    fileSource: string = 'fax',
  ) => attachIncomingDocument({ orderId, faxUuid, uploadTypes, fileSource, rotation: rotation || undefined })

  const statusVariant = (status: IncomingDocumentStatus) => {
    if (status === IncomingDocumentStatus.FAILED) return 'danger'
    if (status === IncomingDocumentStatus.ATTACHED) return 'success'
    if (status === IncomingDocumentStatus.MATCHED) return 'info'
    return 'secondary'
  }

  const header = [
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <span>
          <Button variant="outline-primary" size="sm" href={row.original.mediaUrl} target="_blank">
            <i className="fa fa-eye" css={{ transform: 'translateY(2px)', marginRight: 2 }} />{' '}
            Preview
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="ml-2"
            onClick={() => setAssignDialogFaxId(row.original.uuid)}>
            <i className="fa fa-exchange" css={{ transform: 'translateY(2px)', marginRight: 2 }} />{' '}
            Assign To Order
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="ml-2"
            onClick={() => setCreateDialogFaxId(row.original.uuid)}>
            <i className="fa fa-plus-circle" css={{ transform: 'translateY(2px)', marginRight: 2 }} />{' '}
            Create Order
          </Button>
          <Can permission={UserPermission.DELETE_INCOMING_FAXES}>
            <Button
              variant="outline-danger"
              size="sm"
              className="ml-2"
              onClick={() =>
                confirm('document')
                  .then(() => deleteFax({ uuid: row.original.uuid }))
                  .catch(() => {})
              }>
              <i className="fa fa-trash" css={{ transform: 'translateY(2px)', marginRight: 2 }} />{' '}
              Delete
            </Button>
          </Can>
        </span>
      ),
    },
    {
      Header: 'Source',
      accessor: 'source',
      className: 'col-1',
      Cell: ({ row }) => <span className="text-uppercase">{row.original.source}</span>,
    },
    {
      Header: 'Status',
      accessor: 'status',
      className: 'col-1',
      Cell: ({ row }) => (
        <span className={`badge badge-${statusVariant(row.original.status)}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      Header: 'Order ID',
      accessor: 'orderId',
      className: 'col-1',
      Cell: ({ row }) => <span>{row.original.orderId || '-'}</span>,
    },
    {
      Header: 'Document Type',
      accessor: 'faxType',
      className: 'col-1',
      Cell: ({ row }) => <span>{row.original.faxType || '-'}</span>,
    },
    {
      Header: 'Date Received',
      accessor: 'createdAt',
      Cell: ({ row }) => <span>{row.original.createdAt}</span>,
    },
    {
      Header: 'Error',
      accessor: 'error',
      className: 'col-2',
      Cell: ({ row }) => (
        <div>{row.original.error || '-'}</div>
      ),
    },
  ]

  return (
    <DashboardContent
      title="Incoming Documents"
      icon="fa fa-arrow-down"
      content={
        <div className="card card-primary mt-3">
          {currentFax && (
            <CreateOrderDialog
              show={createDialogFaxId}
              onClose={() => setCreateDialogFaxId('')}
              currentFax={currentFax}
            />
          )}
          <AssignDialog
            show={assignDialogFaxId}
            onSubmit={attachToOrder}
            onClose={() => setAssignDialogFaxId('')}
            faxMediaUrl={currentAssignItem?.mediaUrl}
            fileSource={currentAssignItem?.source}
          />
          <div className="card-body row align-items-center">
            <StyledTableWrapper className="card-body row">
              <DataTable
                tableKey={'incoming_documents'}
                data={incoming}
                status={isLoading}
                tableColumns={header}
                entityName="incoming documents"
                pageSize={incoming?.items?.length}
                setSortOptions={() => {}}
              />
            </StyledTableWrapper>
          </div>
        </div>
      }
      breadcrumb={[
        {
          label: 'Incoming Documents',
        },
      ]}
    />
  )
}

export default IncomingDocuments
